import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ChartHeader from "./ChartHeader";

function ChartDefects({ title, subtitle, data, chartColors, detailsDesc, orderNumber = "", dateRange = ""  }) {
    const defaultData = [{date: 'n/a', A: 'n/a', nok: 'n/a', rework: 'n/a'}];
    const inputData = data || defaultData;

    const defectColors = {
        A: '#FFA07A', B: '#800080', C: '#FFFF00', D: '#808000', E: '#FF0000',
        F: '#FFD700', G: '#00FF00', H: '#008000', I: '#20B2AA', J: '#00BFFF',
        K: '#4169E1', L: '#FF69B4', M: '#FF1493', N: '#00CED1', O: '#FA8072',
        P: '#FF8C00', Q: '#4B0082', R: '#000080', S: '#C0C000', T: '#DC143C',
        U: '#00FFFF', V: '#8B4513', W: '#FF00FF', X: '#00CD96', Y: '#CD5C5C', Z: '#0000FF',
        AA: '#FF7F50', AB: '#6A5ACD', AC: '#ADFF2F', AD: '#228B22', AE: '#B22222',
        AF: '#DAA520', AG: '#32CD32', AH: '#1E90FF', AI: '#9370DB', AJ: '#3CB371',
        AK: '#FF6347', AL: '#2F4F4F', AM: '#E9967A', AN: '#87CEEB', AO: '#F08080',
        AP: '#FF4500', AQ: '#6B8E23', AR: '#483D8B', AS: '#8FBC8F', AT: '#B0C4DE',
        AU: '#00FA9A', AV: '#FF00FF', AW: '#FF1493', AX: '#20B2AA', AY: '#FFB6C1', AZ: '#9932CC',
        a: '#DEB887', b: '#5F9EA0', c: '#7FFF00', d: '#FF69B4', e: '#2C3E50', f: '#E74C3C',
        g: '#F39C12', h: '#16A085', i: '#8E44AD', j: '#27AE60', k: '#2980B9', l: '#D35400',
        m: '#C0392B', n: '#34495E', o: '#F1C40F', p: '#1ABC9C', q: '#9B59B6', r: '#E67E22',
        s: '#95A5A6', t: '#D7BCCB', u: '#00BFFF', v: '#A0522D', w: '#FF00FF', x: '#3CB371',
        y: '#FFD700', z: '#1E90FF',
        aa: '#BC8F8F', ab: '#4682B4', ac: '#F0E68C', ad: '#90EE90', ae: '#CD5C5C', af: '#BDB76B',
        ag: '#66CDAA', ah: '#00BFFF', ai: '#EE82EE', aj: '#98FB98', ak: '#FFB347', al: '#708090',
        am: '#FA8072', an: '#ADD8E6', ao: '#FFA07A', ap: '#FF8C00', aq: '#9ACD32', ar: '#6A5ACD',
        as: '#8FBC8F', at: '#B0C4DE', au: '#00FF7F', av: '#FFB6C1', aw: '#DDA0DD', ax: '#48D1CC',
        ay: '#FFDAB9', az: '#BA55D3'
    };

    const undefinedColor = '#9E9E9E';
    const nokColor = 'red';

    const colors = chartColors || defectColors;
    const finalDefectColors = { ...colors };
    if (finalDefectColors.undefined !== undefined) {
        delete finalDefectColors.undefined;
    }

    const defectTotals = {};
    inputData.forEach(day => {
        Object.keys(day).forEach(key => {
            if (key !== 'date' && key !== 'nok' && key !== 'rework') {
                defectTotals[key] = (defectTotals[key] || 0) + (day[key] || 0);
            }
        });
    });

    const totalParts = inputData.reduce((sum, day) => {
        const dayTotal = Object.keys(day)
            .filter(key => key !== 'date' && key !== 'nok' && key !== 'rework')
            .reduce((daySum, nok) => daySum + (day[nok] || 0), 0);
        return sum + dayTotal;
    }, 0);

    const allNoks = Array.from(new Set(
        inputData.flatMap(item =>
            Object.keys(item).filter(key => {
                if (key === 'date' || key === 'nok' || key === 'rework') return false;
                return item[key] > 0;
            })
        )
    )).sort((a, b) => {
        if (a === 'undefined') return 1;
        if (b === 'undefined') return -1;
        return (defectTotals[b] || 0) - (defectTotals[a] || 0);
    });

    const totalNok = inputData.reduce((sum, day) => {
        return sum + (day.nok || 0);
    }, 0);

    const activeDays = inputData.length;

    const getColorForDefect = (nok) => {
        if (nok === 'undefined') return undefinedColor;
        if (finalDefectColors[nok]) return finalDefectColors[nok];
        return undefinedColor;
    };

    const DefectBarShape = (props) => {
        const { x, y, width, height, fill, payload, dataKey } = props;
        if (!x || !y || !width || !height) return null;

        const padding = 3;
        const elements = [
            <rect key="bar" x={x} y={y} width={width} height={height} fill={fill} />
        ];

        // Rysuj ramkę NOK tylko na DOLNYM segmencie stosu (allNoks[0])
        if (dataKey === allNoks[0] && payload.nok > 0) {
            const thisValue = payload[dataKey] || 0;
            if (thisValue > 0) {
                const pixelsPerUnit = height / thisValue;
                const nokHeight = payload.nok * pixelsPerUnit;
                const baseline = y + height; // dół całego stosu

                elements.push(
                    <rect
                        key="nok-border"
                        x={x - padding}
                        y={baseline - nokHeight}
                        width={width + padding * 2}
                        height={nokHeight}
                        fill="none"
                        stroke={nokColor}
                        strokeWidth={1.5}
                    />
                );
            }
        }

        return <g>{elements}</g>;
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const filteredPayload = [...payload.filter(entry => entry.value > 0 && entry.dataKey !== 'nok')].reverse();

            return (
                <div style={{
                    backgroundColor: 'white',
                    padding: '12px',
                    border: '1px solid #ccc',
                    borderRadius: '6px',
                    fontSize: '13px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    minWidth: '150px'
                }}>
                    <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: 'rgb(52, 71, 103)' }}>
                        {`Date: ${label}`}
                    </p>
                    {filteredPayload.map((entry, index) => (
                        <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '4px'
                        }}>
                            <div style={{
                                width: '10px',
                                height: '10px',
                                backgroundColor: entry.color,
                                marginRight: '6px',
                                borderRadius: '2px'
                            }}></div>
                            <span style={{ color: '#333', fontSize: '12px' }}>
                                {entry.dataKey} : {entry.value} {totalParts > 0 && defectTotals[entry.dataKey] > 0 && `(${(defectTotals[entry.dataKey]*100/totalParts).toFixed(1)}%) `}
                                {detailsDesc && detailsDesc.find(el => el.label === entry.dataKey)?.value}
                            </span>
                        </div>
                    ))}
                    {payload.map((entry, index) => {
                        if (entry.dataKey === 'nok') {
                            return (
                                <div key={index} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: '4px',
                                    paddingTop: '8px',
                                    borderTop: '1px solid #eee'
                                }}>
                                    <div style={{
                                        width: '10px',
                                        height: '10px',
                                        border: '2px solid red',
                                        marginRight: '6px',
                                        borderRadius: '2px'
                                    }}></div>
                                    <span style={{color: '#333', fontSize: '12px', fontWeight: '600'}}>
                                        NOK : {entry.value}
                                    </span>
                                </div>
                            );
                        }
                    })}
                </div>
            );
        }
        return null;
    };

    const CustomLegend = ({ payload }) => {
        return (
            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '16px',
                marginTop: '16px',
                maxHeight: '120px',
                overflowY: 'auto',
                paddingRight: '8px',
                paddingBottom: '4px'
            }}>
                {payload.map((entry, index) => (
                    <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '13px',
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}>
                        <div style={{
                            width: '12px',
                            height: '12px',
                            backgroundColor: entry.color,
                            marginRight: '6px',
                            borderRadius: '2px',
                            ...(entry.dataKey === 'nok' && {
                                backgroundColor: 'transparent',
                                border: '2px solid red'
                            })
                        }}></div>
                        {entry.dataKey === 'nok' ? (
                            <span style={{ color: 'rgb(52, 71, 103)', fontWeight: '500' }}>nok</span>
                        ) : (
                            <span style={{ color: 'rgb(52, 71, 103)', fontWeight: '500' }}>
                                {entry.dataKey} : {defectTotals[entry.dataKey] || 0} {totalParts > 0 && defectTotals[entry.dataKey] > 0 && `(${(defectTotals[entry.dataKey]*100/totalParts).toFixed(1)}%) `}
                                {detailsDesc && detailsDesc.find(el => el.label === entry.dataKey)?.value}
                            </span>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const styles = {
        container: {
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            padding: '24px',
            margin: '16px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid rgba(0, 0, 0, 0.06)'
        },
        chartContainer: {
            backgroundColor: 'rgb(248, 249, 250)',
            borderRadius: '6px',
            padding: '24px',
            marginBottom: '28px',
            minHeight: '400px',
            display: 'flex',
            flexDirection: 'column'
        },
        statsContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            padding: '0 20px'
        },
        statNumber: {
            fontSize: '20px',
            fontWeight: '600',
            color: 'rgb(52, 71, 103)',
            margin: '0',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
        },
        statLabel: {
            fontSize: '13px',
            color: 'rgb(123, 128, 154)',
            margin: '0',
            fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
        }
    };

    return (
        <div style={styles.container}>
            <ChartHeader
                title={title}
                subtitle={subtitle}
                orderName={orderNumber}
                dateRange={dateRange}
            />

            <div style={styles.chartContainer}>
                <div style={styles.statsContainer}>
                    <div style={{ textAlign: 'center' }}>
                        <p style={styles.statNumber}>{activeDays}</p>
                        <p style={styles.statLabel}>Analysis Days</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <p style={styles.statNumber}>{totalNok}</p>
                        <p style={styles.statLabel}>Total NOK</p>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                        <p style={styles.statNumber}>{totalParts}</p>
                        <p style={styles.statLabel}>Total defects no.</p>
                    </div>
                </div>

                <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                        data={inputData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        maxBarSize={60}
                        barCategoryGap="20%"
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                        <XAxis
                            dataKey="date"
                            tick={{ fontSize: 12, fill: 'rgb(123, 128, 154)' }}
                            axisLine={{ stroke: 'rgba(0,0,0,0.2)' }}
                        />
                        <YAxis
                            tick={{ fontSize: 12, fill: 'rgb(123, 128, 154)' }}
                            axisLine={{ stroke: 'rgba(0,0,0,0.2)' }}
                            label={{
                                value: 'Parts Count',
                                angle: -90,
                                position: 'insideLeft',
                                style: { fontSize: '12px', fill: 'rgb(123, 128, 154)' }
                            }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend content={<CustomLegend />} />
                        {/* DEFEKTY + NOK BORDER — jeden stos, bez przesunięć */}
                        {allNoks.map((nok) => (
                            <Bar
                                key={nok}
                                dataKey={nok}
                                stackId="defects"
                                fill={getColorForDefect(nok)}
                                shape={(props) => <DefectBarShape {...props} />}
                            />
                        ))}

                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default ChartDefects;